import { useState } from 'react';
import { motion } from 'framer-motion';
import { Download, Search, Filter, FileText, BookOpen, ClipboardList, HelpCircle, ChevronDown } from 'lucide-react';
import { resources, faqs } from '../data';
import FAQAccordion from '../components/FAQAccordion';

const typeIcons = {
  guide: BookOpen,
  checklist: ClipboardList,
  whitepaper: FileText,
  faq: HelpCircle,
};

const typeLabels = { guide: 'Guide', checklist: 'Checklist', whitepaper: 'Whitepaper', faq: 'FAQ' };

export default function ResourcesPage() {
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const [activeTab, setActiveTab] = useState<'resources' | 'faq'>('resources');

  const categories = ['all', ...Array.from(new Set(resources.map(r => r.category)))];
  const filteredResources = resources.filter(r => {
    const matchesSearch = r.title.toLowerCase().includes(search.toLowerCase()) || r.description.toLowerCase().includes(search.toLowerCase());
    const matchesFilter = activeFilter === 'all' || r.category === activeFilter;
    return matchesSearch && matchesFilter;
  });

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
      {/* Hero */}
      <section className="pt-32 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#050d1a] via-[#0a1628] to-[#0f2240]" />
        <div className="absolute top-20 right-20 w-[500px] h-[500px] bg-[#c9a84c]/5 rounded-full blur-[150px]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="gold-line mb-6" />
          <h1 className="text-4xl md:text-5xl font-bold text-[#e8e4dc] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Legal <span className="text-[#c9a84c]">Resources</span>
          </h1>
          <p className="text-lg text-[#e8e4dc]/60 max-w-2xl">
            Free downloadable guides, checklists, and whitepapers to help you understand your legal options and make informed decisions.
          </p>
        </div>
      </section>

      {/* Tabs */}
      <section className="border-b border-[#c9a84c]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1">
            {[
              { id: 'resources' as const, label: 'Downloadable Resources', icon: Download },
              { id: 'faq' as const, label: 'FAQ', icon: HelpCircle },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-all ${
                  activeTab === tab.id
                    ? 'border-[#c9a84c] text-[#c9a84c]'
                    : 'border-transparent text-[#e8e4dc]/40 hover:text-[#e8e4dc]/70'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {activeTab === 'resources' && (
            <>
              {/* Search and Filter */}
              <div className="flex flex-col md:flex-row gap-4 mb-8">
                <div className="relative flex-1">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#e8e4dc]/30" />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search resources..."
                    className="w-full pl-11 pr-4 py-3 bg-[#0f2240]/40 border border-[#c9a84c]/15 rounded-xl text-sm text-[#e8e4dc] focus:border-[#c9a84c]/40 focus:outline-none transition-colors placeholder:text-[#e8e4dc]/20"
                  />
                </div>
                <div className="flex items-center gap-2 overflow-x-auto pb-1">
                  <Filter className="w-4 h-4 text-[#e8e4dc]/30 shrink-0" />
                  {categories.map((cat) => (
                    <button
                      key={cat}
                      onClick={() => setActiveFilter(cat)}
                      className={`shrink-0 px-4 py-2 rounded-lg text-xs font-medium transition-all ${
                        activeFilter === cat
                          ? 'bg-[#c9a84c]/15 text-[#c9a84c] border border-[#c9a84c]/30'
                          : 'bg-[#0f2240]/30 text-[#e8e4dc]/50 border border-[#c9a84c]/10 hover:border-[#c9a84c]/20'
                      }`}
                    >
                      {cat === 'all' ? 'All Categories' : cat}
                    </button>
                  ))}
                </div>
              </div>

              {/* Resources Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredResources.map((res, idx) => {
                  const TypeIcon = typeIcons[res.type];
                  return (
                    <motion.div
                      key={res.id}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: idx * 0.08 }}
                      className="group p-6 rounded-2xl bg-gradient-to-b from-[#0f2240]/50 to-[#0a1628]/50 border border-[#c9a84c]/10 hover:border-[#c9a84c]/25 hover:shadow-xl hover:shadow-[#c9a84c]/5 transition-all duration-500"
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="w-12 h-12 rounded-xl bg-[#c9a84c]/10 flex items-center justify-center group-hover:bg-[#c9a84c]/20 transition-colors">
                          <TypeIcon className="w-6 h-6 text-[#c9a84c]" />
                        </div>
                        <span className="px-3 py-1 rounded-full bg-[#0a1628]/60 border border-[#c9a84c]/10 text-[10px] font-semibold text-[#c9a84c]/70 uppercase tracking-wider">
                          {typeLabels[res.type]}
                        </span>
                      </div>
                      <span className="text-xs text-[#c9a84c]/60 font-medium">{res.category}</span>
                      <h3 className="text-lg font-bold text-[#e8e4dc] mt-1 mb-3 group-hover:text-[#c9a84c] transition-colors" style={{ fontFamily: 'Playfair Display, serif' }}>
                        {res.title}
                      </h3>
                      <p className="text-sm text-[#e8e4dc]/50 leading-relaxed mb-6 line-clamp-3">{res.description}</p>
                      <div className="flex items-center justify-between">
                        <div className="text-xs text-[#e8e4dc]/30">
                          {res.fileSize} • {res.downloads.toLocaleString()} downloads
                        </div>
                        <button className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#c9a84c]/10 border border-[#c9a84c]/20 text-xs font-medium text-[#c9a84c] hover:bg-[#c9a84c]/20 transition-all group-hover:shadow-md group-hover:shadow-[#c9a84c]/10">
                          <Download className="w-3 h-3" /> Download
                        </button>
                      </div>
                    </motion.div>
                  );
                })}
              </div>

              {filteredResources.length === 0 && (
                <div className="text-center py-16">
                  <Search className="w-10 h-10 text-[#c9a84c]/20 mx-auto mb-4" />
                  <p className="text-sm text-[#e8e4dc]/40">No resources match your search criteria.</p>
                </div>
              )}
            </>
          )}

          {activeTab === 'faq' && (
            <div className="max-w-3xl mx-auto">
              <div className="mb-8 text-center">
                <h2 className="text-2xl font-bold text-[#e8e4dc] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Frequently Asked <span className="text-[#c9a84c]">Questions</span>
                </h2>
                <p className="text-sm text-[#e8e4dc]/50">Find answers to common questions about our services and client portal.</p>
              </div>
              <FAQAccordion faqs={faqs} />
            </div>
          )}
        </div>
      </section>
    </motion.div>
  );
}
